import React, { createContext, useContext, useEffect } from 'react';
import globalSSEService from '../services/globalSSEService';
import { AuthContext } from './AuthContext';
import { sendTabChange } from '../services/api';
import { feLog } from '../utils/frontendLogger';

const GlobalSSEContext = createContext(null);

export function GlobalSSEProvider({ children }) {
    const token = useContext(AuthContext);
    const [isConnected, setIsConnected] = React.useState(false);

    // Store token but don't connect SSE yet.
    // SSE connection is deferred until after initial HTTP data loads
    // (triggered by useV3State via startConnection) to prevent the large
    // v3Init SSE event from blocking the main thread during initial fetch.
    useEffect(() => {
        if (token) {
            globalSSEService.token = token;
        } else {
            globalSSEService.disconnect();
        }
    }, [token]);
    
    // Monitor connection state
    useEffect(() => {
        const handleConnectionStateChange = (connected) => {
            setIsConnected(connected);
        };
        
        globalSSEService.addConnectionStateListener(handleConnectionStateChange);
        
        return () => {
            globalSSEService.removeConnectionStateListener(handleConnectionStateChange);
        };
    }, []);

    // Provide methods to children
    const value = {
        registerHandler: (tab, handler) => globalSSEService.registerHandler(tab, handler),
        unregisterHandler: (tab) => globalSSEService.unregisterHandler(tab),
        setActiveTab: async (tab) => {
            globalSSEService.setActiveTab(tab);
            // Also notify server about tab change
            if (token) {
                try {
                    await sendTabChange(token, tab);
                } catch (error) {
                    console.error('[GlobalSSEProvider] Failed to send tab change:', error);
                }
            }
        },
        startConnection: () => {
            const hasToken = !!globalSSEService.token;
            const hasConnection = !!globalSSEService.connection;
            feLog('info', 'sse_diag.start_connection_called', '', {
                has_token: String(hasToken),
                has_connection: String(hasConnection),
                will_connect: String(hasToken && !hasConnection),
            });
            if (hasToken && !hasConnection) {
                globalSSEService.connect(globalSSEService.token);
            }
        },
        isConnected: () => globalSSEService.getConnectionStatus(),
        connectionState: isConnected, // Expose reactive connection state
    };

    return (
        <GlobalSSEContext.Provider value={value}>
            {children}
        </GlobalSSEContext.Provider>
    );
}

export function useGlobalSSE() {
    const context = useContext(GlobalSSEContext);
    if (!context) {
        throw new Error('useGlobalSSE must be used within GlobalSSEProvider');
    }
    return context;
}